/**
 * useMapLocaleSync (Plan 118 Phase 4 — REWRITTEN 2026-05-08)
 *
 * Patches MapLibre symbol layers' `text-field` expression when the user's
 * i18n locale changes. The hook is decoupled from the text-field shape: caller
 * passes a `buildTextField(locale)` closure and the hook just calls it.
 *
 * Why a builder closure (not a `poiLabel` arg): caller-side `poiLabel`
 * changes (e.g. theme.enabled toggle) shouldn't re-fire this effect — the
 * theme editor already has its own setLayoutProperty path. Stored in a ref
 * so the effect's deps stay stable. Last-write-wins between this and the
 * theme-editor path is acceptable.
 *
 * Style-reload retry: `setLayoutProperty` no-ops when the style isn't loaded
 * (e.g. mid-reload during theme-editor live preview). Defers via one-shot
 * `styledata` listener.
 *
 * Cleanup: removes the i18n listener on unmount. Critical — MapCanvas can
 * unmount on route change; leaked listeners would fire on stale map refs.
 */

import { useEffect, useRef } from "react";
import type maplibregl from "maplibre-gl";
import { useTranslation } from "react-i18next";

/**
 * Map-side surface needed by `applyTextFieldToLayers`. Narrowed for testability
 * — full `maplibregl.Map` is a 600+ method type, fake stubs only need these.
 */
export interface MapLocaleSyncTarget {
  isStyleLoaded(): boolean | void;
  getLayer(id: string): unknown;
  setLayoutProperty(id: string, name: string, value: unknown): void;
  once(event: "styledata", listener: () => void): unknown;
}

/**
 * Apply a text-field expression to all matching symbol layers. If the style is
 * mid-reload, defer via one-shot `styledata` listener (guard-only would
 * silently no-op). Returns false if deferred, true if applied immediately.
 *
 * Pure with respect to `map` + `layerIds` + `expr` — extracted from the hook so
 * the deferral branch is unit-testable without React/maplibre/jsdom infra.
 *
 * `isCancelled` lets the caller short-circuit if the component unmounted
 * between scheduling and firing — prevents `setLayoutProperty` on stale refs.
 */
export function applyTextFieldToLayers(
  map: MapLocaleSyncTarget,
  layerIds: readonly string[],
  expr: unknown,
  isCancelled: () => boolean = () => false
): boolean {
  const setProps = () => {
    if (isCancelled()) return;
    for (const id of layerIds) {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, "text-field", expr);
      }
    }
  };

  if (!map.isStyleLoaded()) {
    map.once("styledata", setProps);
    return false;
  }
  setProps();
  return true;
}

export function useMapLocaleSync(
  map: maplibregl.Map | null,
  layerIds: readonly string[],
  buildTextField: (locale: string) => unknown[]
): void {
  const { i18n } = useTranslation();
  const buildTextFieldRef = useRef(buildTextField);
  buildTextFieldRef.current = buildTextField;

  useEffect(() => {
    if (!map) return;

    let cancelled = false;

    const handler = (newLocale: string) => {
      const expr = buildTextFieldRef.current(newLocale);
      applyTextFieldToLayers(map, layerIds, expr, () => cancelled);
    };

    i18n.on("languageChanged", handler);

    return () => {
      cancelled = true;
      i18n.off("languageChanged", handler);
    };
  }, [map, layerIds, i18n]);
}
